import { ChevronRight, ShoppingBag, CheckCircle2, AlertCircle, MapPin } from 'lucide-react';
import type { OrderPillTone } from '@/lib/orderPill';

interface OrderStatusPillProps {
  label: string;
  tone: OrderPillTone;
  /** 3-character pick-up code, for pick-up orders. */
  code?: string | null;
  onOpen: () => void;
}

const BACKGROUND: Record<OrderPillTone, string> = {
  neutral: 'rgba(0,0,0,0.45)',
  ready: 'rgba(16,185,129,0.85)',
  done: 'rgba(0,0,0,0.45)',
  alert: 'rgba(239,68,68,0.75)',
};

const ICON: Record<OrderPillTone, typeof ShoppingBag> = {
  neutral: ShoppingBag,
  ready: MapPin,
  done: CheckCircle2,
  alert: AlertCircle,
};

/**
 * Compact order status shown under the match header on every screen outside
 * the ordering view — the same shape as the half-time pre-order strip. Until
 * now a fan who went back to the home screen (or reloaded onto it) had no way
 * to see or reopen their order.
 */
export function OrderStatusPill({ label, tone, code, onOpen }: OrderStatusPillProps) {
  const Icon = ICON[tone];
  return (
    <button
      type="button"
      onClick={onOpen}
      data-tone={tone}
      aria-label={label}
      className="relative z-10 mt-4 w-full max-w-md mx-auto flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl text-left text-white text-sm font-semibold backdrop-blur-sm transition-colors"
      style={{ backgroundColor: BACKGROUND[tone] }}
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">{label}</span>
      </span>
      <span className="flex items-center gap-2 flex-shrink-0">
        {code && <span className="font-mono tracking-widest text-xs px-2 py-0.5 rounded bg-white/20">{code}</span>}
        <ChevronRight className="w-4 h-4" />
      </span>
    </button>
  );
}
